import React from 'react';
import { Link } from 'react-router-dom';
import { FaUserShield, FaUserGraduate, FaChalkboardTeacher } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const RoleSelection = () => {
    return (
        <>
            <Helmet>
                <title>Choose Your Role | GallaMandi Management System</title>
            </Helmet>

            <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4 relative overflow-hidden font-sans">
                {/* Animated Background Elements - Vivid Blobs */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-purple-600 rounded-full mix-blend-screen filter blur-[100px] opacity-40 animate-blob"></div>
                    <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600 rounded-full mix-blend-screen filter blur-[100px] opacity-40 animate-blob animation-delay-2000"></div>
                    <div className="absolute bottom-[-10%] left-[20%] w-[500px] h-[500px] bg-pink-600 rounded-full mix-blend-screen filter blur-[100px] opacity-40 animate-blob animation-delay-4000"></div>
                    {/* Noise Overlay for texture */}
                    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100 contrast-150"></div>
                </div>

                <div className="relative z-10 w-full max-w-7xl mx-auto flex flex-col items-center justify-center">

                    {/* Header Section */}
                    <div className="text-center mb-16 animate-slide-down relative">
                        <h1 className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 via-purple-200 to-pink-200 mb-6 tracking-tight drop-shadow-[0_0_15px_rgba(168,85,247,0.5)]">
                            GallaMandi Management
                        </h1>
                        <h2 className="text-2xl md:text-3xl font-light text-blue-100 mb-4 tracking-widest uppercase">
                            Select Your Portal
                        </h2>
                        <div className="h-1 w-32 bg-gradient-to-r from-transparent via-cyan-400 to-transparent mx-auto rounded-full opacity-80 shadow-[0_0_10px_rgba(34,211,238,0.8)]"></div>
                    </div>

                    {/* Cards Container */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full px-4 perspective-1000">

                        {/* Admin Card */}
                        <RoleCard
                            to="/admin/login"
                            icon={<FaUserShield className="w-16 h-16 text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]" />}
                            title="Admin Portal"
                            description="Manage the entire ecosystem. Access full control over students, staff, and system configuration."
                            delay="0s"
                            color="cyan"
                        />

                        {/* Student Card */}
                        <RoleCard
                            to="/login"
                            icon={<FaUserGraduate className="w-16 h-16 text-purple-300 drop-shadow-[0_0_10px_rgba(192,132,252,0.8)]" />}
                            title="Student Zone"
                            description="Access learning materials, check grades, and submit assignments in your personal learning hub."
                            delay="0.2s"
                            color="purple"
                        />

                        {/* Teacher Card */}
                        <RoleCard
                            to="/company/login"
                            icon={<FaChalkboardTeacher className="w-16 h-16 text-pink-300 drop-shadow-[0_0_10px_rgba(244,114,182,0.8)]" />}
                            title="Teacher Dashboard"
                            description="Plan curriculum, grade assessments, and track student progress seamlessly."
                            delay="0.4s"
                            color="pink"
                        />

                    </div>

                    <div className="mt-16 text-slate-400 text-sm animate-fade-in opacity-0 flex items-center gap-2" style={{ animationFillMode: 'forwards', animationDelay: '1s' }}>
                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                        System Operational • v2.0
                    </div>
                </div>
            </div>

            {/* Styles for Animations & Utilities */}
            <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 10s infinite alternate;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes slide-down {
          from { opacity: 0; transform: translateY(-40px); filter: blur(10px); }
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        .animate-slide-down {
          animation: slide-down 1s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(60px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
        @keyframes fade-in {
           from { opacity: 0; }
           to { opacity: 1; }
        }
      `}</style>
        </>
    );
};

const RoleCard = ({ to, icon, title, description, delay, color }) => {
    const colorClasses = {
        cyan: 'hover:shadow-cyan-500/30 hover:border-cyan-400/50 group-hover:text-cyan-300',
        purple: 'hover:shadow-purple-500/30 hover:border-purple-400/50 group-hover:text-purple-300',
        pink: 'hover:shadow-pink-500/30 hover:border-pink-400/50 group-hover:text-pink-300',
    };

    return (
        <Link
            to={to}
            className={`group relative bg-white/5 backdrop-blur-xl rounded-[2rem] p-8 border border-white/10 shadow-xl transition-all duration-500 hover:-translate-y-4 hover:scale-105 overflow-hidden opacity-0 ${colorClasses[color]}`}
            style={{
                animation: `fade-in-up 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) ${delay} forwards`
            }}
        >
            {/* Glow Effect */}
            <div className={`absolute -inset-1 bg-gradient-to-r from-${color}-500 to-${color}-600 rounded-[2rem] opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500`}></div>

            {/* Content */}
            <div className="flex flex-col items-center text-center relative z-10 h-full justify-between">
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-500 p-4 bg-white/5 rounded-full border border-white/10 ring-1 ring-white/20 shadow-[inset_0_0_20px_rgba(255,255,255,0.05)]">
                    {icon}
                </div>

                <div className="mb-4">
                    <h3 className="text-2xl font-bold text-white mb-3 tracking-wide group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300 transition-all">
                        {title}
                    </h3>

                    <p className="text-blue-100/70 leading-relaxed text-sm font-light">
                        {description}
                    </p>
                </div>

                <div className={`mt-6 py-2 px-6 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium tracking-wider group-hover:bg-${color}-500/20 group-hover:border-${color}-400/50 group-hover:shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-all duration-300 flex items-center gap-2`}>
                    ENTER PORTAL
                    <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </div>
            </div>

            {/* Shine Effect */}
            <div className="absolute top-0 -left-[100%] w-[50%] h-full bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-[-25deg] group-hover:animate-[shine_1s_infinite] transition-all"></div>
            <style>{`
        @keyframes shine {
            100% { left: 200%; }
        }
      `}</style>
        </Link>
    );
};

export default RoleSelection;
